import { get } from 'lodash'
import { useEffect, useState } from 'react'

import { useLazyGetRouteTimestampsQuery } from 'api/route/getRouteStops'
import { toRequestableRouteIds } from 'components/common/map/requestableRouteIds'
import { createDateFromString, formatDateToBEFormatDateFns, isNotInvalidDate } from 'utils/date'

export interface RouteLocationTimestamp {
  lat: number
  lng: number
  timestamp: number
  speedMps?: number
}

interface UseRoutesDriverPathTimestampsParams {
  routeIds?: string[]
  date?: Date | string | null
  enabled?: boolean
}

// GPS timestamp samples ({ lat, lng, timestamp, speedMps }) for a set of routes on
// an arbitrary date, keyed by route. Pairs with useRoutesDriverPathBreadcrumb on the
// bulk maps so each route's path surfaces its own hover tooltip (time / speed / driver).
export const useRoutesDriverPathTimestamps = ({
  routeIds = [],
  date,
  enabled = true,
}: UseRoutesDriverPathTimestampsParams): Record<string, RouteLocationTimestamp[]> | null => {
  const [timestampsByRouteId, setTimestampsByRouteId] = useState<Record<string, RouteLocationTimestamp[]> | null>(null)
  const [getRouteTimestamps] = useLazyGetRouteTimestampsQuery()

  const idsKey = toRequestableRouteIds(routeIds).join(',')
  // Callers may pass a service date as a string; date-fns `format` throws on a
  // non-Date, so coerce first (createDateFromString avoids the UTC off-by-one).
  const dateObj = typeof date === 'string' ? createDateFromString(date) : date
  const beDate = isNotInvalidDate(dateObj) ? formatDateToBEFormatDateFns(dateObj) : null

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : []
    if (!enabled || ids.length === 0 || !beDate) {
      setTimestampsByRouteId(null)
      return undefined
    }
    let cancelled = false
    getRouteTimestamps({ routes: ids, serviceDate: beDate })
      .unwrap()
      .then((data: unknown) => {
        if (cancelled) return
        const byRoute = get(data, 'routeLocationTimestamps', {}) as Record<string, RouteLocationTimestamp[]>
        const entries = ids
          .map(id => [id, get(byRoute, id, []) as RouteLocationTimestamp[]] as const)
          .filter(([, samples]) => samples.length > 0)
        setTimestampsByRouteId(entries.length > 0 ? Object.fromEntries(entries) : null)
      })
      .catch(() => {
        if (!cancelled) setTimestampsByRouteId(null)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, idsKey, beDate, getRouteTimestamps])

  return timestampsByRouteId
}

export default useRoutesDriverPathTimestamps
